/**
 * Preferences — shape, defaults, storage helpers.
 *
 * Decision 4: localStorage only. No backend API yet (T-PREF-API TODO).
 * Decision D1: Reset only, no Save. Each pref write commits immediately.
 * Decision D3: ThemeMode mapped to theme-provider's Theme type; other prefs drive HTML data attrs.
 * Decision D6: bnNumerals drives data-bn-numerals on <html>.
 * Decision D10: bnTypography 'mixed' = emphasis-via-serif.
 *
 * Theme accent palette: each accent ID maps to [data-accent="<id>"] selector blocks in
 * packages/ui/src/styles/globals.css that override the --red token family (--red, --red-soft,
 * --red-soft-strong, --red-ink, --red-ink-strong, --red-line) for both light and dark modes.
 * paper-red uses the :root defaults (no override selector needed).
 */

export type ThemeMode = 'system' | 'light' | 'dark';
export type AccentColor = 'paper-red' | 'evergreen' | 'midnight' | 'plum' | 'amber';
export type BnTypography = 'siliguri' | 'serif' | 'mixed';
export type Density = 'compact' | 'comfortable' | 'spacious';

export type Preferences = {
  themeMode: ThemeMode;
  accent: AccentColor;
  bnTypography: BnTypography;
  density: Density;
  bnNumerals: boolean;
  bilingualLabels: boolean;
  shortcutsEnabled: boolean;
  vimMode: boolean;
  autoCollapseRequirements: boolean;
};

export const DEFAULT_PREFERENCES: Preferences = {
  themeMode: 'system',
  accent: 'paper-red',
  bnTypography: 'siliguri',
  density: 'comfortable',
  bnNumerals: true,
  bilingualLabels: true,
  shortcutsEnabled: true,
  vimMode: false,
  autoCollapseRequirements: true,
};

/**
 * 5-color accent palette (Decision D3 / spec § 4.2).
 * All accents are visually active. [data-accent="<id>"] selectors in
 * packages/ui/src/styles/globals.css override the --red token family per accent.
 */
export const ACCENT_PALETTE: { id: AccentColor; hex: string; bnLabel: string }[] = [
  { id: 'paper-red', hex: '#9c1f1c', bnLabel: 'কাগজ-লাল' },
  { id: 'evergreen', hex: '#1f6b3c', bnLabel: 'চিরসবুজ' },
  { id: 'midnight', hex: '#2d3b8c', bnLabel: 'মধ্যরাত' },
  { id: 'plum', hex: '#7a2c5c', bnLabel: 'পল্লব' },
  { id: 'amber', hex: '#a36418', bnLabel: 'অম্বর' },
];

const STORAGE_KEY = 'qm.prefs';
const THEME_MODES = ['system', 'light', 'dark'] as const;
const ACCENT_COLORS: readonly AccentColor[] = ACCENT_PALETTE.map((accent) => accent.id);
const BN_TYPOGRAPHY_MODES = ['siliguri', 'serif', 'mixed'] as const;
const DENSITIES = ['compact', 'comfortable', 'spacious'] as const;

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && allowed.includes(value as T);
}

function sanitizeStoredPreferences(value: unknown): Partial<Preferences> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const stored = value as Record<string, unknown>;
  const sanitized: Partial<Preferences> = {};

  if (isOneOf(stored.themeMode, THEME_MODES)) sanitized.themeMode = stored.themeMode;
  if (isOneOf(stored.accent, ACCENT_COLORS)) sanitized.accent = stored.accent;
  if (isOneOf(stored.bnTypography, BN_TYPOGRAPHY_MODES)) {
    sanitized.bnTypography = stored.bnTypography;
  }
  if (isOneOf(stored.density, DENSITIES)) sanitized.density = stored.density;
  if (typeof stored.bnNumerals === 'boolean') sanitized.bnNumerals = stored.bnNumerals;
  if (typeof stored.bilingualLabels === 'boolean') {
    sanitized.bilingualLabels = stored.bilingualLabels;
  }
  if (typeof stored.shortcutsEnabled === 'boolean') {
    sanitized.shortcutsEnabled = stored.shortcutsEnabled;
  }
  if (typeof stored.vimMode === 'boolean') sanitized.vimMode = stored.vimMode;
  if (typeof stored.autoCollapseRequirements === 'boolean') {
    sanitized.autoCollapseRequirements = stored.autoCollapseRequirements;
  }

  return sanitized;
}

/**
 * Read persisted preferences from localStorage, merging with defaults so that
 * newly added keys always get their default value even on older stored blobs.
 */
export function getStoredPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed = sanitizeStoredPreferences(JSON.parse(raw));
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

/** Persist the full preferences object. */
export function storePreferences(prefs: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* Safari private mode or storage quota — accept ephemeral state */
  }
}

/** Remove the stored preferences blob (used by Reset). */
export function clearPreferences(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Deep-equal check against defaults — used to conditionally hide Reset button. */
export function isAtDefaults(prefs: Preferences): boolean {
  return JSON.stringify(prefs) === JSON.stringify(DEFAULT_PREFERENCES);
}
